import { memo } from "react";
import { Link } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import {
  formatCurrency,
  formatDateTime,
  formatPhone,
} from "@/shared/lib/formatters";
import { RentalStatusBadge } from "./RentalStatusBadge";
import { RENTAL_TYPE_LABELS } from "./RentalTypeBadge";
import type {
  RentalPaymentMethod,
  RentalResponseDTO,
} from "../types/rental.types";

const PAYMENT_METHOD_LABEL: Record<RentalPaymentMethod, string> = {
  PIX: "PIX",
  CREDIT_CARD: "Crédito",
  DEBIT_CARD: "Débito",
  CASH: "Dinheiro",
};

interface RentalDetailsModalProps {
  open: boolean;
  rental: RentalResponseDTO | null;
  onClose: () => void;
}

interface FieldProps {
  label: string;
  children: React.ReactNode;
}

const Field = memo(function Field({ label, children }: FieldProps) {
  return (
    <div className="space-y-0.5">
      <p className="text-xs text-muted-foreground">{label}</p>
      <div className="text-sm font-medium">{children}</div>
    </div>
  );
});

/**
 * Modal read-only de detalhe de aluguel — exibe todos os campos do
 * `Rental`, incluindo peças (com photoUrl), itens beca, payments e
 * tags. Sem ações — disparadas pela tabela.
 *
 * @example
 * <RentalDetailsModal
 *   open={dialogMode === 'details'}
 *   rental={selected}
 *   onClose={closeDialog}
 * />
 */
export const RentalDetailsModal = memo(function RentalDetailsModal({
  open,
  rental,
  onClose,
}: RentalDetailsModalProps) {
  return (
    <Dialog open={open} onOpenChange={(value) => !value && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {rental ? `Aluguel #${rental.id}` : "Aluguel"}
          </DialogTitle>
          <DialogDescription>
            {rental
              ? `${rental.clientName} · Contrato ${rental.contractNumber}`
              : ""}
          </DialogDescription>
        </DialogHeader>

        {rental ? (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <Field label="Status">
                <RentalStatusBadge status={rental.status} />
              </Field>
              <Field label="Tipo">
                {RENTAL_TYPE_LABELS[rental.rentalType]}
              </Field>
              <Field label="Vendedor">{rental.sellerName}</Field>
              <Field label="Cliente">{rental.clientName}</Field>
              <Field label="Telefone">{formatPhone(rental.clientPhone)}</Field>
              <Field label="Contrato">{rental.contractNumber}</Field>
              <Field label="Valor total">
                {formatCurrency(rental.totalValue)}
              </Field>
              <Field label="Valor pago">
                {formatCurrency(rental.paidValue)}
              </Field>
              <Field label="Sapatos">
                {rental.hasShoes
                  ? `${rental.shoesPairQuantity} ${rental.shoesPairQuantity === 1 ? "par" : "pares"}`
                  : "Não"}
              </Field>
              <Field label="Saída prevista">
                {formatDateTime(rental.expectedOutAt)}
              </Field>
              <Field label="Retorno previsto">
                {formatDateTime(rental.expectedInAt)}
              </Field>
              <Field label="Data da locação">
                {formatDateTime(rental.rentedAt)}
              </Field>
              <Field label="Saída real">
                {rental.outAt ? formatDateTime(rental.outAt) : "—"}
              </Field>
              <Field label="Retorno real">
                {rental.inAt ? formatDateTime(rental.inAt) : "—"}
              </Field>
            </div>

            {rental.description && (
              <Field label="Observações">
                <p className="text-sm font-normal whitespace-pre-wrap">
                  {rental.description}
                </p>
              </Field>
            )}

            {rental.rentalType === "SUIT" &&
              (rental.stockItems ?? []).length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground">
                    Peças vinculadas ({(rental.stockItems ?? []).length})
                  </p>
                  <div className="flex flex-col gap-2">
                    {(rental.stockItems ?? []).map((item) => (
                      <Link
                        key={item.id}
                        to={`/estoque/${item.id}`}
                        className="flex items-center gap-3 rounded-md border border-input bg-background px-3 py-2 text-sm hover:bg-accent transition-colors"
                      >
                        {item.photoUrl ? (
                          <img
                            src={item.photoUrl}
                            alt=""
                            className="w-10 h-10 rounded object-cover"
                          />
                        ) : null}
                        <span className="flex-1 truncate">
                          {item.barcode} · {item.productBrand}{" "}
                          {item.productColor}
                          {" · "}
                          {item.productName} · Tam. {item.size}
                        </span>
                        <ExternalLink
                          className="w-3.5 h-3.5 text-muted-foreground shrink-0"
                          aria-hidden="true"
                        />
                      </Link>
                    ))}
                  </div>
                </div>
              )}

            {rental.rentalType === "GOWN" &&
              (rental.gownItems ?? []).length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground">
                    Itens beca ({(rental.gownItems ?? []).length})
                  </p>
                  <div className="space-y-1">
                    {(rental.gownItems ?? []).map((item) => (
                      <Link
                        key={item.idGownRentalItem}
                        to={`/produtos/${item.productId}`}
                        className="flex items-center gap-3 rounded-md border border-input bg-background px-3 py-2 text-sm hover:bg-accent transition-colors"
                      >
                        {item.photoUrl ? (
                          <img
                            src={item.photoUrl}
                            alt=""
                            className="w-10 h-10 rounded object-cover"
                          />
                        ) : null}
                        <span className="flex-1 truncate">
                          {item.productName}
                        </span>
                        <span className="text-muted-foreground">
                          {item.quantity} un.
                          {item.returnedQuantity > 0
                            ? ` · ${item.returnedQuantity} devolvidos`
                            : ""}
                        </span>
                        <ExternalLink
                          className="w-3.5 h-3.5 text-muted-foreground shrink-0"
                          aria-hidden="true"
                        />
                      </Link>
                    ))}
                  </div>
                </div>
              )}

            {(rental.payments ?? []).length > 0 && (
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground">
                  Pagamentos ({(rental.payments ?? []).length})
                </p>
                <div className="space-y-1">
                  {(rental.payments ?? []).map((payment, index) => (
                    <div
                      key={`${payment.paymentMethod}-${index}`}
                      className="flex items-center gap-2 rounded-md border border-input bg-background px-3 py-2 text-sm"
                    >
                      <span className="flex-1">
                        {PAYMENT_METHOD_LABEL[payment.paymentMethod]}
                      </span>
                      <span className="text-muted-foreground">
                        {formatCurrency(payment.paidValue)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
});
